export type QuickFilterRadioRule<T = Record<string, any>> = {
  id: string;
  match: (filter: T) => boolean;
};

const CUSTOMER_MODULE_PATHS = [
  "/registration/customers",
  "/registration/address",
  "/registration/communications",
  "/registration/promoter-director",
  "/registration/key-management",
  "/registration/partner"
];

/** True while still inside customer master or its child views. */
export function isCustomerModulePath(pathname = window.location.pathname): boolean {
  return CUSTOMER_MODULE_PATHS.some(path => pathname.includes(path));
}

const BUYER_SELLER_MODULE_PATHS = [
  "/registration/buyer-seller",
  "/registration/customer-beneficiary",
  "/registration/customer-beneficiary-programme"
];

/** True while still inside buyer-seller master or its child views. */
export function isBuyerSellerModulePath(pathname = window.location.pathname): boolean {
  return BUYER_SELLER_MODULE_PATHS.some(path => pathname.includes(path));
}

const INSTRUMENT_BATCH_MODULE_PATHS = [
  "/transaction/instrument-batch",
  "/transaction/instrument/detail",
  "/transaction/staging-batch/detail",
  "/transaction/staging-instrument/detail",
  "/transaction/payment-batch/detail",
  "/transaction/payment-instrument/detail"
];

/** True while still inside instrument batch or its child views. */
export function isInstrumentBatchModulePath(pathname = window.location.pathname): boolean {
  return INSTRUMENT_BATCH_MODULE_PATHS.some(path => pathname.includes(path));
}

const STAGING_BATCH_MODULE_PATHS = [
  "/transaction/staging-batch",
  "/transaction/staging-instrument/detail",
  "/transaction/payment-batch/detail",
  "/transaction/payment-instrument/detail"
];

/** True while still inside staging batch or its child views. */
export function isStagingBatchModulePath(pathname = window.location.pathname): boolean {
  return STAGING_BATCH_MODULE_PATHS.some(path => pathname.includes(path));
}

const PAYMENT_BATCH_MODULE_PATHS = [
  "/transaction/payment-batch",
  "/transaction/payment-instrument/detail"
];

/** True while still inside payment batch or its child views. */
export function isPaymentBatchModulePath(pathname = window.location.pathname): boolean {
  return PAYMENT_BATCH_MODULE_PATHS.some(path => pathname.includes(path));
}

/**
 * Syncs quick-filter radio inputs from persisted/restored filter state.
 * Clears all radios in `rules`, then checks the first matching rule.
 * Reusable across list screens — pass module-specific rules.
 */
export function syncQuickFilterRadios<T extends Record<string, any>>(
  filter: T,
  rules: QuickFilterRadioRule<T>[]
): void {
  const selectedId = rules.find(rule => rule.match(filter))?.id ?? null;

  rules.forEach(rule => {
    const el = document.getElementById(rule.id) as HTMLInputElement | null;
    if (el) {
      el.checked = rule.id === selectedId;
    }
  });
}

/** Default processing-status filter used when a list has no user-applied filters. */
export function buildDefaultProcessingStatusFilter(statuses: string[]) {
  if (!statuses?.length) {
    return undefined;
  }
  return {
    processingStatus: [
      {
        value: `com.vayana.vlms.flora.txns.lib.ProcessingStatus:${statuses.join(";")}`,
        matchMode: "inEnum",
        operator: "and"
      }
    ]
  };
}
